import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Chip,
  Paper,
  Divider,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DOCS, DOCS_COPY, findDoc, type Doc } from './docsContent.js';
import { renderDoc, type TocEntry } from './docsRender.js';
import type { Lang } from './landingCopy.js';
import { setLang } from '../i18n/lang.js';

const EMERALD = '#2ec2b3';
const INK = '#0a0f16';

/** Typography and chrome for the rendered Markdown body. */
const PROSE = {
  color: 'text.primary',
  fontSize: 16,
  lineHeight: 1.75,
  '& > *:first-of-type': { mt: 0 },

  '& h1': { fontSize: { xs: 30, md: 38 }, fontWeight: 800, letterSpacing: -1, lineHeight: 1.15, mt: 0, mb: 2, color: INK },
  '& h2': {
    fontSize: { xs: 23, md: 27 },
    fontWeight: 750,
    letterSpacing: -0.5,
    lineHeight: 1.25,
    mt: 7,
    mb: 2,
    pb: 1.25,
    color: INK,
    borderBottom: '1px solid',
    borderColor: 'divider',
    scrollMarginTop: 96,
  },
  '& h3': { fontSize: { xs: 17, md: 19 }, fontWeight: 700, mt: 4.5, mb: 1.25, color: INK, scrollMarginTop: 96 },
  '& h4': { fontSize: 16, fontWeight: 700, mt: 3, mb: 1, color: INK, scrollMarginTop: 96 },

  // The anchor sits in the left gutter and only appears on hover, so it never
  // competes with the heading itself.
  '& .anchor': {
    float: 'left',
    ml: '-1.1em',
    pr: '0.35em',
    color: EMERALD,
    opacity: 0,
    textDecoration: 'none',
    fontWeight: 400,
    transition: 'opacity .15s',
  },
  '& h2:hover .anchor, & h3:hover .anchor, & h4:hover .anchor': { opacity: 1 },

  '& p': { my: 2 },
  '& strong': { fontWeight: 700, color: INK },
  '& a': { color: '#1a8f83', textDecorationColor: 'rgba(46,194,179,0.4)', '&:hover': { color: EMERALD } },

  '& ul, & ol': { pl: 3, my: 2 },
  '& li': { my: 0.75 },
  '& li > ul, & li > ol': { my: 0.75 },
  '& li::marker': { color: EMERALD },

  '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', my: 6 },

  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.875em',
    bgcolor: 'rgba(46,194,179,0.12)',
    color: '#0f6f66',
    px: '0.4em',
    py: '0.15em',
    borderRadius: 1,
    wordBreak: 'break-word',
  },
  '& pre': {
    bgcolor: INK,
    color: '#dbe4e3',
    p: 2.5,
    borderRadius: 2,
    overflowX: 'auto',
    my: 3,
    fontSize: 13.5,
    lineHeight: 1.65,
    border: '1px solid rgba(255,255,255,0.08)',
  },
  '& pre code': { bgcolor: 'transparent', color: 'inherit', p: 0, fontSize: 'inherit' },

  '& blockquote': {
    m: 0,
    my: 3,
    px: 2.5,
    py: 1.5,
    borderLeft: `3px solid ${EMERALD}`,
    bgcolor: 'rgba(46,194,179,0.06)',
    borderRadius: '0 8px 8px 0',
    '& p': { my: 0.5 },
  },

  // Tables carry a lot of this guide (sizing, permissions, issuers), so they
  // get the same care as the running text rather than browser defaults.
  '& .table-scroll': { overflowX: 'auto', my: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 },
  '& table': { borderCollapse: 'collapse', width: '100%', fontSize: 14.5, fontVariantNumeric: 'tabular-nums' },
  '& thead th': {
    textAlign: 'left',
    fontWeight: 700,
    color: INK,
    bgcolor: '#f1f4f4',
    px: 2,
    py: 1.25,
    whiteSpace: 'nowrap',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& tbody td': { px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', verticalAlign: 'top' },
  '& tbody tr:hover': { bgcolor: 'rgba(46,194,179,0.04)' },
} as const;

/** Wraps every table so a wide one scrolls inside its own box, not the page. */
function wrapTables(html: string): string {
  return html.replace(/<table>/g, '<div class="table-scroll"><table>').replace(/<\/table>/g, '</table></div>');
}

export function DocsPage({ lang = 'en' }: { lang?: Lang }) {
  const { slug } = useParams();
  const doc = findDoc(slug);
  return doc ? <DocBody doc={doc} lang={lang} /> : <DocsIndex lang={lang} />;
}

// ── Shell ────────────────────────────────────────────────────────────────

export function DocsShell({ lang, children }: { lang: Lang; children: React.ReactNode }) {
  const c = DOCS_COPY[lang];
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const home = lang === 'tr' ? '/tr' : '/';
  // Carry the choice to the pages whose URL has no language in it.
  useEffect(() => {
    setLang(lang);
  }, [lang]);
  // Switching language keeps you on the same document rather than dropping you
  // back at the index — the page you were reading exists in both.
  const otherLang =
    lang === 'tr' ? pathname.replace(/^\/tr/, '') || '/docs' : `/tr${pathname}`;
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          bgcolor: 'rgba(246,247,249,0.85)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Stack direction="row" alignItems="center" spacing={1} sx={{ height: 64 }}>
            <PlayCircleIcon sx={{ color: EMERALD }} />
            <Typography
              component={RouterLink}
              to={home}
              variant="h6"
              sx={{ fontWeight: 800, color: INK, textDecoration: 'none' }}
            >
              Replay
              <Box component="span" sx={{ color: EMERALD }}>
                Ops
              </Box>
            </Typography>
            <Chip
              label={c.eyebrow}
              size="small"
              sx={{ ml: 1, bgcolor: 'rgba(46,194,179,0.12)', color: '#1a8f83', fontWeight: 600 }}
            />
            <Box sx={{ flexGrow: 1 }} />
            {/* EN | TR, the same two-letter toggle the rest of the product
                uses. A full language name reads as a link to something else. */}
            <Stack direction="row" alignItems="center" aria-label={c.language}>
              <Button
                component={RouterLink}
                to={lang === 'en' ? pathname : otherLang}
                size="small"
                aria-current={lang === 'en' ? 'page' : undefined}
                sx={{
                  minWidth: 40,
                  px: 1,
                  fontWeight: 700,
                  color: lang === 'en' ? INK : 'text.disabled',
                  '&:hover': { color: INK, bgcolor: 'transparent' },
                }}
              >
                EN
              </Button>
              <Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />
              <Button
                component={RouterLink}
                to={lang === 'tr' ? pathname : otherLang}
                size="small"
                aria-current={lang === 'tr' ? 'page' : undefined}
                sx={{
                  minWidth: 40,
                  px: 1,
                  fontWeight: 700,
                  color: lang === 'tr' ? INK : 'text.disabled',
                  '&:hover': { color: INK, bgcolor: 'transparent' },
                }}
              >
                TR
              </Button>
            </Stack>
            <Button
              startIcon={<ArrowBackIcon />}
              component={RouterLink}
              to={home}
              sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'inline-flex' } }}
            >
              {c.backToSite}
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/register')}
              sx={{ bgcolor: EMERALD, color: INK, '&:hover': { bgcolor: '#22988c' } }}
            >
              {c.getStarted}
            </Button>
          </Stack>
        </Container>
      </Box>
      {children}
      <Box sx={{ bgcolor: INK, color: 'rgba(255,255,255,0.6)', mt: 10 }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Stack direction="row" spacing={1} alignItems="center">
              <PlayCircleIcon sx={{ color: EMERALD, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700 }}>
                ReplayOps
              </Typography>
            </Stack>
            <Typography variant="caption">© {new Date().getFullYear()} ReplayOps</Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

// ── Index ────────────────────────────────────────────────────────────────

function DocsIndex({ lang }: { lang: Lang }) {
  const c = DOCS_COPY[lang];
  const prefix = lang === 'tr' ? '/tr/docs' : '/docs';
  useEffect(() => {
    document.title = `${c.eyebrow} · ReplayOps`;
  }, [c.eyebrow]);

  return (
    <DocsShell lang={lang}>
      <Box sx={{ background: 'linear-gradient(180deg, #eef5f4 0%, #f6f7f9 100%)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="md" sx={{ py: { xs: 7, md: 10 } }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 34, md: 48 }, letterSpacing: -1.5, lineHeight: 1.1, color: INK, mb: 2 }}>
            {c.title}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: { xs: 16, md: 19 }, maxWidth: 680 }}>
            {c.subtitle}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* The planner first: most people arriving here want to know what to
            run and on what, not to read 25 minutes of prose to find out. */}
        <Paper
          component={RouterLink}
          to={`${prefix}/setup`}
          sx={{
            p: 3,
            mb: 4,
            display: 'block',
            textDecoration: 'none',
            bgcolor: INK,
            color: 'rgba(255,255,255,0.75)',
            transition: 'transform .15s',
            '&:hover': { transform: 'translateY(-2px)' },
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <AutoAwesomeIcon sx={{ color: EMERALD, mt: 0.25 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                {c.wizardTitle}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75 }}>
                {c.wizardBody}
              </Typography>
            </Box>
            <ArrowForwardIcon sx={{ color: EMERALD }} />
          </Stack>
        </Paper>

        <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5 }}>
          {c.documents}
        </Typography>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {DOCS.map((d) => (
            <Paper
              key={d.slug}
              component={RouterLink}
              to={`${prefix}/${d.slug}`}
              sx={{
                p: 3,
                display: 'block',
                textDecoration: 'none',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'border-color .15s, transform .15s',
                '&:hover': { borderColor: EMERALD, transform: 'translateY(-2px)' },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <MenuBookIcon sx={{ color: EMERALD, mt: 0.25 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: INK }}>
                      {d.title[lang]}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {d.summary[lang]}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                    <ScheduleIcon sx={{ fontSize: 15 }} />
                    <Typography variant="caption">{c.readingTime(d.minutes)}</Typography>
                  </Stack>
                </Box>
                <ArrowForwardIcon sx={{ color: 'text.disabled' }} />
              </Stack>
            </Paper>
          ))}
        </Stack>

        <Paper sx={{ mt: 5, p: 3, bgcolor: INK, color: 'rgba(255,255,255,0.75)' }}>
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
            {c.needHelp}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2.5 }}>
            {c.needHelpBody}
          </Typography>
          <Button
            href="mailto:hello@replayops.ai"
            variant="contained"
            sx={{ bgcolor: EMERALD, color: INK, '&:hover': { bgcolor: '#22988c' } }}
          >
            {c.contact}
          </Button>
        </Paper>
      </Container>
    </DocsShell>
  );
}

// ── A document ───────────────────────────────────────────────────────────

function DocBody({ doc, lang }: { doc: Doc; lang: Lang }) {
  const c = DOCS_COPY[lang];
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const [tocOpen, setTocOpen] = useState(false);
  const body = doc.body[lang];
  const { html, toc } = useMemo(() => {
    const r = renderDoc(body);
    return { html: wrapTables(r.html), toc: r.toc };
  }, [body]);
  const [active, setActive] = useState('');

  useEffect(() => {
    document.title = `${doc.title[lang]} · ReplayOps`;
  }, [doc, lang]);

  // Highlight the section being read. Without it a 25-minute document gives no
  // sense of where you are in it.
  useEffect(() => {
    const headings = toc
      .map((t) => document.getElementById(t.id))
      .filter((el): el is HTMLElement => !!el);
    if (headings.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    headings.forEach((h) => obs.observe(h));
    return () => obs.disconnect();
  }, [toc, html]);

  // Keep the highlighted entry visible: in a document with 45 headings the
  // active one is otherwise somewhere above or below the rail's own scroll.
  useEffect(() => {
    if (!active) return;
    const el = document.querySelector(`[data-toc="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const tocList = (
    <Box component="nav" sx={{ py: 1 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, px: 1 }}>
        {c.onThisPage}
      </Typography>
      <Stack sx={{ mt: 1 }}>
        {toc.map((t: TocEntry) => (
          <Box
            key={t.id}
            component="a"
            data-toc={t.id}
            href={`#${t.id}`}
            onClick={() => setTocOpen(false)}
            sx={{
              display: 'block',
              px: 1,
              py: 0.6,
              pl: t.level === 3 ? 2.5 : 1,
              fontSize: t.level === 3 ? 13 : 13.5,
              lineHeight: 1.4,
              textDecoration: 'none',
              color: active === t.id ? '#1a8f83' : 'text.secondary',
              fontWeight: active === t.id ? 700 : 400,
              borderLeft: '2px solid',
              borderColor: active === t.id ? EMERALD : 'transparent',
              '&:hover': { color: INK, bgcolor: 'rgba(46,194,179,0.06)' },
            }}
          >
            {t.text}
          </Box>
        ))}
      </Stack>
    </Box>
  );

  const prefix = lang === 'tr' ? '/tr/docs' : '/docs';

  return (
    <DocsShell lang={lang}>
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack direction="row" spacing={5} alignItems="flex-start">
          {/* Left rail: the other documents. */}
          {wide && (
            <Box component="aside" sx={{ width: 210, flexShrink: 0, position: 'sticky', top: 88 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, px: 1 }}>
                {c.documents}
              </Typography>
              <Stack sx={{ mt: 1 }}>
                {DOCS.map((d) => (
                  <Box
                    key={d.slug}
                    component={RouterLink}
                    to={`${prefix}/${d.slug}`}
                    sx={{
                      px: 1,
                      py: 0.8,
                      fontSize: 14,
                      textDecoration: 'none',
                      borderRadius: 1,
                      color: d.slug === doc.slug ? INK : 'text.secondary',
                      fontWeight: d.slug === doc.slug ? 700 : 400,
                      bgcolor: d.slug === doc.slug ? 'rgba(46,194,179,0.10)' : 'transparent',
                      '&:hover': { color: INK },
                    }}
                  >
                    {d.title[lang]}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* The document. */}
          <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: 820 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip size="small" icon={<ScheduleIcon />} label={c.readingTime(doc.minutes)} variant="outlined" />
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                href={doc.rawPath[lang]}
                download
                sx={{ color: 'text.secondary' }}
              >
                {c.download}
              </Button>
              {!wide && (
                <Button size="small" startIcon={<ListAltIcon />} onClick={() => setTocOpen(true)} sx={{ color: 'text.secondary' }}>
                  {c.onThisPage}
                </Button>
              )}
            </Stack>

            <Divider sx={{ mb: 4 }} />
            <Box sx={PROSE} dangerouslySetInnerHTML={{ __html: html }} />
          </Box>

          {/* Right rail: where you are. */}
          {wide && (
            <Box
              sx={{
                width: 250,
                flexShrink: 0,
                position: 'sticky',
                top: 88,
                maxHeight: 'calc(100vh - 110px)',
                overflowY: 'auto',
              }}
            >
              {tocList}
            </Box>
          )}
        </Stack>
      </Container>

      <Drawer anchor="right" open={tocOpen} onClose={() => setTocOpen(false)}>
        <Box sx={{ width: 290, p: 1.5 }}>
          <Stack direction="row" justifyContent="flex-end">
            <IconButton size="small" onClick={() => setTocOpen(false)}>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Stack>
          {tocList}
        </Box>
      </Drawer>
    </DocsShell>
  );
}
